import React from 'react'
import Navbar from '../components/navbar'
import Footer from '../components/footer'
import { Link } from 'react-router-dom'

export default function PrivacyPolicy() {
  return (
    <>
      <Navbar />
      <section className="relative table w-full py-36 bg-[url('../../assets/images/bg/bg1.svg')] bg-bottom bg-no-repeat">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900"></div>
        <div className="container">
          <div className="grid grid-cols-1 pb-8 text-center mt-10">
            <h3 className="md:text-3xl text-2xl md:leading-snug tracking-wide leading-snug font-medium text-white">
              Cookie &amp; Privacy Policy
            </h3>
            <p className="text-slate-400">Last Updated: July 30, 2026</p>
          </div>
        </div>
        <div className="absolute text-center z-10 bottom-5 start-0 end-0 mx-3">
          <ul className="breadcrumb tracking-[0.5px] breadcrumb-light mb-0 inline-block">
            <li className="inline breadcrumb-item text-[15px] font-semibold duration-500 ease-in-out text-white/50 hover:text-white">
              <Link to="/">NIFTY</Link>
            </li>
            <li className="inline breadcrumb-item text-[15px] font-semibold duration-500 ease-in-out text-white" aria-current="page">
              Cookie &amp; Privacy Policy
            </li>
          </ul>
        </div>
      </section>
      <div className="relative">
        <div className="shape absolute start-0 end-0 sm:-bottom-px -bottom-[2px] overflow-hidden z-1 text-white dark:text-slate-900">
          <svg className="w-full h-auto" viewBox="0 0 2880 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 48H1437.5H2880V0H2160C1442.5 52 720 0 720 0H0V48Z" fill="currentColor"></path>
          </svg>
        </div>
      </div>
      <section className="relative md:py-24 py-16">
        <div className="container">
          <div className="md:flex justify-center">
            <div className="md:w-3/4">
              <div className="p-6 bg-white dark:bg-slate-900 shadow dark:shadow-gray-800 rounded-md">

                {/* 1. Introduction */}
                <h5 className="text-xl font-semibold mb-4">1. Introduction</h5>
                <p className="text-slate-400">
                  This Cookie &amp; Privacy Policy explains how JADEFIRST LIMITED ("JADEFIRST", "we", "us", or "our") collects, uses, stores, shares, and protects personal data when you visit or use NIFTY at{" "}
                  <a href="https://www.nifty-art.com/" className="underline text-violet-600 hover:text-violet-500">https://www.nifty-art.com/</a>{" "}
                  and any related services we provide (collectively, the "Services").
                </p>
                <p className="text-slate-400 mt-3">
                  NIFTY is a curated online platform through which customers can purchase digital artworks represented as non-fungible tokens ("NFTs"). The artists and artworks made available through the Services are independently sourced, reviewed, and selected by us. The Services do not provide an open user-listing marketplace.
                </p>
                <p className="text-slate-400 mt-3">
                  For personal data processed through the Services, the data controller is:
                </p>
                <p className="text-slate-400 mt-2">
                  JADEFIRST LIMITED<br />
                  Company No. 15932126<br />
                  Registered address: 72 Halliwick Road, London, England, N10 1AB<br />
                  Email:{" "}
                  <a href="mailto:info@nifty-art.com" className="underline text-violet-600 hover:text-violet-500">
                    info@nifty-art.com
                  </a>
                </p>
                <p className="text-slate-400 mt-3">
                  This Policy applies to personal data processed by us through the Services. It does not govern personal data independently processed by third-party payment providers, wallet providers, blockchain networks, or other external services.
                </p>

                {/* 2. Personal data we collect */}
                <h5 className="text-xl font-semibold mb-4 mt-8">2. Personal Data We Collect</h5>
                <p className="text-slate-400">
                  We collect only the personal data reasonably necessary to operate, secure, and improve the Services, complete purchases, deliver digital artworks, provide customer support, and comply with applicable legal obligations.
                </p>

                <h6 className="text-base font-semibold mb-2 mt-6">2.1 Account and contact data</h6>
                <p className="text-slate-400">
                  When you create or use an account, contact us, or make a purchase, we may collect:
                </p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>email address;</li>
                  <li>username or display name, where available;</li>
                  <li>account authentication and security information;</li>
                  <li>communication preferences;</li>
                  <li>information contained in customer support requests or other correspondence.</li>
                </ul>

                <h6 className="text-base font-semibold mb-2 mt-6">2.2 Purchase and transaction data</h6>
                <p className="text-slate-400">
                  When you purchase a digital artwork, we may collect or generate:
                </p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>order details, purchase amount, currency, date, and payment status;</li>
                  <li>purchased artwork and NFT identifiers;</li>
                  <li>purchase and refund history;</li>
                  <li>connected wallet address;</li>
                  <li>blockchain transaction hash, timestamp, network, token identifier, and delivery status;</li>
                  <li>records required for customer support, accounting, fraud prevention, and dispute resolution.</li>
                </ul>

                <h6 className="text-base font-semibold mb-2 mt-6">2.3 Payment and verification data</h6>
                <p className="text-slate-400">
                  Card payments are processed through third-party payment providers. Depending on the payment flow, we may receive limited payment-related information such as:
                </p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>payment confirmation or failure status;</li>
                  <li>transaction reference;</li>
                  <li>payment amount and currency;</li>
                  <li>masked card details or card type;</li>
                  <li>billing information;</li>
                  <li>fraud-prevention, authentication, chargeback, or risk indicators.</li>
                </ul>
                <p className="text-slate-400 mt-3">
                  We do not intentionally request that customers send full card numbers, card security codes, private wallet keys, seed phrases, or recovery phrases to us.
                </p>
                <p className="text-slate-400 mt-3">
                  Where reasonably necessary, we or our service providers may request additional information for payment authentication, fraud prevention, sanctions screening, identity verification, dispute handling, or compliance with legal or payment-provider requirements.
                </p>

                <h6 className="text-base font-semibold mb-2 mt-6">2.4 Wallet and blockchain data</h6>
                <p className="text-slate-400">
                  Where you connect a supported third-party wallet or receive an NFT, we may process:
                </p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>public wallet address;</li>
                  <li>token and smart-contract identifiers;</li>
                  <li>blockchain transaction and delivery data;</li>
                  <li>publicly available wallet activity relevant to the transaction;</li>
                  <li>information required to verify delivery, ownership, authenticity, security, or suspected misuse.</li>
                </ul>
                <p className="text-slate-400 mt-3">
                  Wallet addresses and blockchain transaction data may constitute personal data where they can be linked to an identifiable individual.
                </p>

                <h6 className="text-base font-semibold mb-2 mt-6">2.5 Technical and usage data</h6>
                <p className="text-slate-400">
                  When you access the Services, we may automatically collect:
                </p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>IP address;</li>
                  <li>browser type and version;</li>
                  <li>device type, operating system, and language settings;</li>
                  <li>timestamps, session identifiers, and referral URLs;</li>
                  <li>pages viewed, navigation activity, and interaction data;</li>
                  <li>error logs, security logs, and diagnostic information;</li>
                  <li>cookie identifiers and consent preferences.</li>
                </ul>

                <h6 className="text-base font-semibold mb-2 mt-6">2.6 Information received from third parties</h6>
                <p className="text-slate-400">We may receive information from:</p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>payment processors and acquiring partners;</li>
                  <li>wallet and blockchain infrastructure providers;</li>
                  <li>fraud-prevention, cybersecurity, and identity-verification providers;</li>
                  <li>hosting, analytics, and technical service providers;</li>
                  <li>publicly accessible blockchain records;</li>
                  <li>professional advisers, competent authorities, or other parties where legally permitted.</li>
                </ul>

                <h6 className="text-base font-semibold mb-2 mt-6">2.7 Special-category data</h6>
                <p className="text-slate-400">
                  We do not intentionally request special-category personal data, such as health information, biometric identification data, political opinions, religious beliefs, or information about sexual orientation. Please do not provide such information unless it is specifically requested and legally necessary.
                </p>

                {/* 3. How and why we use personal data */}
                <h5 className="text-xl font-semibold mb-4 mt-8">3. How and Why We Use Personal Data</h5>
                <p className="text-slate-400">We may use personal data for the following purposes:</p>

                <h6 className="text-base font-semibold mb-2 mt-6">3.1 Providing the Services</h6>
                <p className="text-slate-400">
                  We use account, contact, purchase, payment, wallet, and transaction data to:
                </p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>create and administer customer accounts;</li>
                  <li>authenticate users and maintain account security;</li>
                  <li>process and confirm purchases;</li>
                  <li>transfer purchased NFTs to the wallet designated by the customer;</li>
                  <li>provide transaction confirmations and service communications;</li>
                  <li>administer refunds, cancellations, complaints, and chargebacks;</li>
                  <li>provide customer support;</li>
                  <li>maintain transaction and ownership records.</li>
                </ul>
                <p className="text-slate-400 mt-3">
                  The principal legal basis for this processing is performance of a contract or taking steps at the customer's request before entering into a contract.
                </p>

                <h6 className="text-base font-semibold mb-2 mt-6">3.2 Security, fraud prevention, and risk management</h6>
                <p className="text-slate-400">We may use personal data to:</p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>prevent unauthorized access, fraud, abuse, and malicious activity;</li>
                  <li>verify transactions and investigate suspicious activity;</li>
                  <li>protect customers, the Company, payment partners, and service providers;</li>
                  <li>enforce our Terms &amp; Conditions;</li>
                  <li>manage payment disputes and chargebacks;</li>
                  <li>conduct sanctions, fraud, identity, or transaction-risk checks where necessary.</li>
                </ul>
                <p className="text-slate-400 mt-3">
                  The legal bases for this processing may include our legitimate interests, compliance with legal obligations, and the establishment, exercise, or defence of legal claims.
                </p>

                <h6 className="text-base font-semibold mb-2 mt-6">3.3 Legal, regulatory, and accounting obligations</h6>
                <p className="text-slate-400">We may process and retain data to:</p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>maintain accounting and tax records;</li>
                  <li>respond to lawful requests from courts, regulators, law-enforcement bodies, or other competent authorities;</li>
                  <li>comply with applicable corporate, consumer-protection, data-protection, sanctions, fraud-prevention, and financial-crime obligations;</li>
                  <li>establish, exercise, or defend legal claims;</li>
                  <li>maintain evidence of purchases, deliveries, refunds, and communications.</li>
                </ul>
                <p className="text-slate-400 mt-3">
                  The legal basis is compliance with legal obligations and, where applicable, our legitimate interests in maintaining appropriate business and legal records.
                </p>

                <h6 className="text-base font-semibold mb-2 mt-6">3.4 Operating and improving the Services</h6>
                <p className="text-slate-400">We may use technical, usage, and aggregated data to:</p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>operate and maintain the website;</li>
                  <li>analyse performance and usage;</li>
                  <li>diagnose errors and technical issues;</li>
                  <li>improve functionality, security, and user experience;</li>
                  <li>measure the effectiveness of platform features;</li>
                  <li>plan and manage our business operations.</li>
                </ul>
                <p className="text-slate-400 mt-3">
                  The legal basis is our legitimate interests, except where consent is required for the use of non-essential cookies or similar technologies.
                </p>

                <h6 className="text-base font-semibold mb-2 mt-6">3.5 Communications</h6>
                <p className="text-slate-400">We may use your contact details to send:</p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>order confirmations;</li>
                  <li>security notices;</li>
                  <li>account and service updates;</li>
                  <li>responses to support requests;</li>
                  <li>material changes to legal terms or policies;</li>
                  <li>other communications necessary to provide the Services.</li>
                </ul>
                <p className="text-slate-400 mt-3">
                  Where we send optional marketing communications, we will do so only where permitted by law and, where required, with your consent. You may unsubscribe from marketing communications at any time.
                </p>

                <h6 className="text-base font-semibold mb-2 mt-6">3.6 Consent-based processing</h6>
                <p className="text-slate-400">
                  Where we rely on consent, including for certain non-essential cookies or optional marketing, you may withdraw your consent at any time. Withdrawal does not affect processing carried out before consent was withdrawn.
                </p>

                {/* 4. Payment processing */}
                <h5 className="text-xl font-semibold mb-4 mt-8">4. Payment Processing</h5>
                <p className="text-slate-400">
                  Payments may be processed by independent third-party payment providers. These providers may collect and process payment card details, billing information, authentication data, device information, and fraud-risk information under their own privacy notices and legal responsibilities.
                </p>
                <p className="text-slate-400 mt-3">
                  We generally receive transaction status and limited payment information rather than complete card credentials. We are not responsible for the independent privacy practices of payment providers, although we select and use providers appropriate for the Services.
                </p>

                {/* 5. Blockchain data */}
                <h5 className="text-xl font-semibold mb-4 mt-8">5. Blockchain Data</h5>
                <p className="text-slate-400">
                  NFT transfers are recorded on public blockchain networks. Blockchain records may include wallet addresses, transaction hashes, token identifiers, smart-contract addresses, timestamps, and transfer history.
                </p>
                <p className="text-slate-400 mt-3">
                  Public blockchains are designed to be transparent, distributed, and generally immutable. Once information has been recorded on a blockchain, we may be unable to alter, erase, restrict, or remove it. Blockchain networks operate independently of JADEFIRST LIMITED, and information recorded on them may remain publicly accessible indefinitely.
                </p>
                <p className="text-slate-400 mt-3">
                  You are responsible for ensuring that the wallet address provided for delivery is accurate and under your control. We will never ask you to disclose a private key, seed phrase, or recovery phrase.
                </p>

                {/* 6. How we share personal data */}
                <h5 className="text-xl font-semibold mb-4 mt-8">6. How We Share Personal Data</h5>
                <p className="text-slate-400">We do not sell personal data.</p>
                <p className="text-slate-400 mt-3">
                  We may share personal data only where reasonably necessary with:
                </p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>payment processors, acquiring banks, and payment-service providers;</li>
                  <li>hosting, cloud, cybersecurity, analytics, and technical infrastructure providers;</li>
                  <li>wallet-connectivity and blockchain infrastructure providers;</li>
                  <li>fraud-prevention, identity-verification, sanctions-screening, and risk-management providers;</li>
                  <li>customer-support and communications providers;</li>
                  <li>auditors, accountants, insurers, lawyers, and other professional advisers;</li>
                  <li>regulators, courts, law-enforcement bodies, tax authorities, and other competent authorities where required or legally permitted;</li>
                  <li>purchasers, investors, advisers, or successors in connection with a genuine merger, acquisition, restructuring, financing, or sale of all or part of our business.</li>
                </ul>
                <p className="text-slate-400 mt-3">
                  Service providers acting on our behalf may process personal data only for the relevant service and subject to appropriate contractual, confidentiality, security, and data-protection obligations.
                </p>
                <p className="text-slate-400 mt-3">
                  Certain blockchain information is shared publicly by the operation of the relevant blockchain network and may be accessed by blockchain participants, explorers, analytics services, and other third parties.
                </p>

                {/* 7. International data transfers */}
                <h5 className="text-xl font-semibold mb-4 mt-8">7. International Data Transfers</h5>
                <p className="text-slate-400">
                  Some service providers or blockchain infrastructure may process personal data outside the United Kingdom or the European Economic Area.
                </p>
                <p className="text-slate-400 mt-3">
                  Where data-protection law requires safeguards for an international transfer, we use an appropriate lawful transfer mechanism. Depending on the destination and circumstances, this may include:
                </p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>an applicable adequacy decision or regulation;</li>
                  <li>the UK International Data Transfer Agreement;</li>
                  <li>the UK Addendum to the European Commission's Standard Contractual Clauses;</li>
                  <li>the European Commission's Standard Contractual Clauses;</li>
                  <li>another legally recognised safeguard or derogation.</li>
                </ul>
                <p className="text-slate-400 mt-3">
                  Where required, we also assess the circumstances of the transfer and implement additional contractual, organisational, or technical safeguards.
                </p>

                {/* 8. Cookies and similar technologies */}
                <h5 className="text-xl font-semibold mb-4 mt-8">8. Cookies and Similar Technologies</h5>

                <h6 className="text-base font-semibold mb-2 mt-6">8.1 What cookies are</h6>
                <p className="text-slate-400">
                  Cookies are small text files stored on a browser or device. We may also use similar technologies, such as local storage, pixels, tags, and session identifiers.
                </p>

                <h6 className="text-base font-semibold mb-2 mt-6">8.2 Categories of cookies</h6>
                <p className="text-slate-400">We may use the following categories:</p>
                <p className="text-slate-400 mt-3">
                  <span className="font-semibold text-slate-500 dark:text-slate-300">Strictly necessary cookies.</span>{" "}
                  These cookies are required for core website functions such as security, account authentication, session management, navigation, payment flow, fraud prevention, and recording cookie preferences. They cannot ordinarily be disabled through our consent tool because the Services may not function properly without them.
                </p>
                <p className="text-slate-400 mt-3">
                  <span className="font-semibold text-slate-500 dark:text-slate-300">Functional cookies.</span>{" "}
                  These cookies remember choices such as language, interface preferences, and wallet-connection status. Where required by law, they are used only after consent.
                </p>
                <p className="text-slate-400 mt-3">
                  <span className="font-semibold text-slate-500 dark:text-slate-300">Analytics and performance cookies.</span>{" "}
                  These cookies help us understand website traffic, usage patterns, errors, and performance so that we can improve the Services. Where required by law, they are used only after consent.
                </p>
                <p className="text-slate-400 mt-3">
                  <span className="font-semibold text-slate-500 dark:text-slate-300">Marketing cookies.</span>{" "}
                  Where used, these cookies may help measure campaigns or provide more relevant advertising. They are not placed without the consent required by applicable law.
                </p>

                <h6 className="text-base font-semibold mb-2 mt-6">8.3 Cookie consent and settings</h6>
                <p className="text-slate-400">
                  Non-essential cookies and similar technologies are not used until you provide the required consent.
                </p>
                <p className="text-slate-400 mt-3">
                  You can accept, reject, or manage non-essential cookie categories through the cookie banner or cookie settings control available on the website. You may return to the cookie settings control and withdraw or change your consent at any time.
                </p>
                <p className="text-slate-400 mt-3">
                  The{" "}
                  <Link to="/cookiepolicy" className="underline text-violet-600 hover:text-violet-500">
                    cookie settings interface
                  </Link>{" "}
                  provides current information about the cookie categories and technologies used on the website. You may also restrict cookies through your browser settings, although this may affect certain website functions.
                </p>

                <h6 className="text-base font-semibold mb-2 mt-6">8.4 Third-party technologies</h6>
                <p className="text-slate-400">
                  Third-party providers may set or access cookies where their services are integrated into the website. Their processing may also be governed by their own privacy and cookie notices.
                </p>

                {/* 9. Data retention */}
                <h5 className="text-xl font-semibold mb-4 mt-8">9. Data Retention</h5>
                <p className="text-slate-400">
                  We retain personal data only for as long as reasonably necessary for the purposes described in this Policy.
                </p>
                <p className="text-slate-400 mt-3">
                  Retention periods depend on the type of information, the reason it is processed, legal and accounting requirements, security needs, dispute or chargeback periods, and whether the information is required to establish or defend legal claims.
                </p>
                <p className="text-slate-400 mt-3">In general:</p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>account information may be retained while an account remains active and for a reasonable period after closure;</li>
                  <li>purchase, payment, delivery, accounting, and tax records are generally retained for up to six years from the end of the relevant company financial year, or longer where legally required;</li>
                  <li>customer-support, complaint, refund, and dispute records may be retained for the period necessary to resolve the matter and protect legal rights;</li>
                  <li>security, fraud-prevention, and technical logs are retained for periods proportionate to the relevant risk and operational need;</li>
                  <li>cookie consent records are retained for the period necessary to demonstrate and manage consent;</li>
                  <li>public blockchain records may remain available indefinitely and are not controlled by us.</li>
                </ul>
                <p className="text-slate-400 mt-3">
                  When personal data is no longer required, we take reasonable steps to delete, anonymise, or securely dispose of it, except where it remains on an independent public blockchain or must be retained by law.
                </p>

                {/* 10. Data security */}
                <h5 className="text-xl font-semibold mb-4 mt-8">10. Data Security</h5>
                <p className="text-slate-400">
                  We use reasonable technical and organisational measures designed to protect personal data against accidental or unlawful loss, destruction, alteration, disclosure, access, or misuse.
                </p>
                <p className="text-slate-400 mt-3">These measures may include:</p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>access controls and authentication;</li>
                  <li>encryption in transit and, where appropriate, at rest;</li>
                  <li>restricted access based on business need;</li>
                  <li>logging, monitoring, and security testing;</li>
                  <li>secure hosting and infrastructure;</li>
                  <li>incident-response and vulnerability-management procedures;</li>
                  <li>confidentiality and data-protection obligations for relevant personnel and service providers.</li>
                </ul>
                <p className="text-slate-400 mt-3">
                  No online system or blockchain-based service can be guaranteed to be completely secure. Customers must protect their account credentials and wallet credentials and must never disclose private keys, seed phrases, or recovery phrases.
                </p>

                {/* 11. Your data-protection rights */}
                <h5 className="text-xl font-semibold mb-4 mt-8">11. Your Data-Protection Rights</h5>
                <p className="text-slate-400">
                  Depending on the applicable law and circumstances, you may have the right to:
                </p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>request access to your personal data;</li>
                  <li>request correction of inaccurate or incomplete personal data;</li>
                  <li>request erasure of personal data;</li>
                  <li>request restriction of processing;</li>
                  <li>object to processing based on legitimate interests;</li>
                  <li>object to direct marketing;</li>
                  <li>request data portability;</li>
                  <li>withdraw consent where processing is based on consent;</li>
                  <li>complain to a competent data-protection authority;</li>
                  <li>request information about applicable international-transfer safeguards.</li>
                </ul>
                <p className="text-slate-400 mt-3">
                  These rights are subject to legal limitations and exceptions. For example, we may retain certain information where required for tax, accounting, fraud-prevention, legal, security, or dispute-resolution purposes. We may also be unable to alter or delete information independently recorded on a public blockchain.
                </p>
                <p className="text-slate-400 mt-3">
                  To exercise a right, contact{" "}
                  <a href="mailto:info@nifty-art.com" className="underline text-violet-600 hover:text-violet-500">
                    info@nifty-art.com
                  </a>. We may request information reasonably necessary to verify your identity and protect personal data from unauthorised disclosure.
                </p>
                <p className="text-slate-400 mt-3">
                  We will respond within the period required by applicable law.
                </p>

                {/* 12. Automated decision-making */}
                <h5 className="text-xl font-semibold mb-4 mt-8">12. Automated Decision-Making</h5>
                <p className="text-slate-400">
                  We do not currently make decisions based solely on automated processing that produce legal or similarly significant effects on customers.
                </p>
                <p className="text-slate-400 mt-3">
                  Payment processors, fraud-prevention providers, or other service providers may independently use automated risk tools under their own policies. Where a transaction is declined or restricted, additional review or information may be requested where available and appropriate.
                </p>

                {/* 13. Children */}
                <h5 className="text-xl font-semibold mb-4 mt-8">13. Children</h5>
                <p className="text-slate-400">
                  The Services are intended only for individuals aged 18 or older.
                </p>
                <p className="text-slate-400 mt-3">
                  We do not knowingly permit individuals under 18 to create accounts or purchase digital artworks through the Services. If we become aware that we have collected personal data from an individual under 18 contrary to this requirement, we will take reasonable steps to restrict the account and delete the data where legally and technically possible.
                </p>

                {/* 14. Third-party services and links */}
                <h5 className="text-xl font-semibold mb-4 mt-8">14. Third-Party Services and Links</h5>
                <p className="text-slate-400">
                  The Services may link to or integrate with third-party payment providers, wallet providers, blockchain networks, blockchain explorers, infrastructure providers, analytics tools, and other external services.
                </p>
                <p className="text-slate-400 mt-3">
                  These services operate independently and may process personal data under their own terms and privacy notices. We do not control public blockchain networks or the independent processing activities of third parties.
                </p>
                <p className="text-slate-400 mt-3">
                  You should review the applicable third-party terms and privacy notices before providing information, connecting a wallet, or using an external service.
                </p>

                {/* 15. Changes to this policy */}
                <h5 className="text-xl font-semibold mb-4 mt-8">15. Changes to This Policy</h5>
                <p className="text-slate-400">
                  We may update this Policy to reflect changes in the Services, legal requirements, business operations, security practices, or third-party integrations.
                </p>
                <p className="text-slate-400 mt-3">
                  The updated version will be published on the website with a revised "Last Updated" date. Where required by law, we will provide additional notice of material changes.
                </p>

                {/* 16. Complaints */}
                <h5 className="text-xl font-semibold mb-4 mt-8">16. Complaints</h5>
                <p className="text-slate-400">
                  Please contact us first at{" "}
                  <a href="mailto:info@nifty-art.com" className="underline text-violet-600 hover:text-violet-500">
                    info@nifty-art.com
                  </a>{" "}
                  if you have a question or complaint concerning personal data.
                </p>
                <p className="text-slate-400 mt-3">You may also have the right to complain to:</p>
                <ul className="list-disc list-outside ps-5 text-slate-400 mt-3 space-y-1">
                  <li>the UK Information Commissioner's Office ("ICO"); or</li>
                  <li>the competent data-protection authority in the country where you live, work, or believe an infringement occurred.</li>
                </ul>
                <p className="text-slate-400 mt-3">
                  Contacting us does not limit your right to complain directly to a competent authority.
                </p>

                {/* 17. Contact us */}
                <h5 className="text-xl font-semibold mb-4 mt-8">17. Contact Us</h5>
                <p className="text-slate-400">
                  Questions, requests, or complaints regarding this Policy may be sent to:
                </p>
                <p className="text-slate-400 mt-2">
                  JADEFIRST LIMITED<br />
                  Company No. 15932126<br />
                  Registered address: 72 Halliwick Road, London, England, N10 1AB<br />
                  Email:{" "}
                  <a href="mailto:info@nifty-art.com" className="underline text-violet-600 hover:text-violet-500">
                    info@nifty-art.com
                  </a>
                </p>
                <p className="text-slate-400 mt-3">
                  You may also reach us through our{" "}
                  <Link to="/contact" className="underline text-violet-600 hover:text-violet-500">
                    Contact Us
                  </Link>{" "}
                  page.
                </p>

              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </>
  )
}
